import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PesapalStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | success | pending | failed | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trackingId = params.get("OrderTrackingId") || params.get("orderTrackingId");
    if (!trackingId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setState("pending"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/pesapal/status/${trackingId}`);
        const s = (data.payment_status || "").toUpperCase();
        if (s === "COMPLETED") {
          setAmount(data.amount);
          setState("success");
          clear();
          return;
        }
        if (s === "FAILED" || s === "INVALID" || s === "REVERSED") { setState("failed"); return; }
        setTimeout(poll, 2500);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[80vh] pt-24 flex items-center justify-center bg-background px-5">
      <div className="max-w-md w-full bg-white border border-brand/10 rounded-2xl p-10 text-center shadow-sm" data-testid="pesapal-status">
        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 text-brand-500 animate-spin mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">Please wait a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-brand-500 mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6" data-testid="pesapal-success">Payment successful!</h1>
            <p className="text-muted-foreground mt-2">
              Thank you for your purchase{amount ? ` of ${amount}` : ""}. A confirmation will follow shortly.
            </p>
            <Button onClick={() => navigate("/shop")} className="mt-8 rounded-full bg-brand hover:bg-brand-700 text-white">
              <ShoppingBag className="h-4 w-4 mr-2" /> Continue Shopping
            </Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock className="h-16 w-16 text-amber-500 mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6">Payment processing</h1>
            <p className="text-muted-foreground mt-2">
              Your payment is still being processed. We'll confirm it shortly by email — no need to pay again.
            </p>
            <Button onClick={() => navigate("/shop")} className="mt-8 rounded-full bg-brand hover:bg-brand-700 text-white">Back to Shop</Button>
          </>
        )}
        {(state === "failed" || state === "error") && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6">Payment not completed</h1>
            <p className="text-muted-foreground mt-2">
              Your payment could not be completed or verified. Please try again.
            </p>
            <Button onClick={() => navigate("/shop")} className="mt-8 rounded-full bg-brand hover:bg-brand-700 text-white">Back to Shop</Button>
          </>
        )}
      </div>
    </div>
  );
}
